import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { Box } from '../../atoms/Box'
import { Icon } from '../../atoms/Icon'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu'
import { FilterDisplay } from '../../molecules/FilterDisplay'
import type { FilterItem } from '../../molecules/FilterDisplay'
import { FilterSelect } from '../../molecules/FilterPanel/FilterFormComponents'
import {
  DemographicsContainer,
  ContentContainer,
  LoadingContainer,
  ErrorContainer,
  FilterSection,
  FilterBar,
  LocationFilters,
  FilterChip as StyledFilterChip,
  LocationBadge,
  Divider,
} from './DemographicsContent.styles'

type LocationType = 'city' | 'district' | 'radius' | 'walk' | 'bicycle' | 'car'

export interface DemographicsContentProps {
  data: any
  loading: boolean
  error: string | null
  activeTab: string
  onTabChange: (tab: string) => void
  locationType: LocationType
  onLocationTypeChange: (type: LocationType) => void
  radiusDistance: number
  onRadiusDistanceChange: (distance: number) => void
  walkTime: number
  onWalkTimeChange: (time: number) => void
  bicycleTime: number
  onBicycleTimeChange: (time: number) => void
  carTime: number
  onCarTimeChange: (time: number) => void
  renderTabContent: () => React.ReactNode
  getLocationLabel: () => string
  /** Actual city name to display in filter chip (e.g., "Carouge") */
  cityName?: string
  /** Actual district name to display in filter chip (e.g., "Genève") */
  districtName?: string
  /** When true, hides filter controls and shows only the current filter value */
  readOnly?: boolean
}

const getLocationIcon = (type: LocationType) => {
  switch (type) {
    case 'city':
      return 'building-2'
    case 'district':
      return 'map'
    case 'walk':
      return 'footprints'
    case 'bicycle':
      return 'bike'
    case 'car':
      return 'car'
    default:
      return 'map-pin'
  }
}

export const DemographicsContent: React.FC<DemographicsContentProps> = ({
  data,
  loading,
  error,
  activeTab,
  onTabChange,
  locationType,
  onLocationTypeChange,
  radiusDistance,
  onRadiusDistanceChange,
  walkTime,
  onWalkTimeChange,
  bicycleTime,
  onBicycleTimeChange,
  carTime,
  onCarTimeChange,
  renderTabContent,
  getLocationLabel,
  cityName,
  districtName,
  readOnly = false,
}) => {
  const { t } = useTranslation('demographics')
  const demographicsTabs = useMemo(
    () => [
      { id: 'overview', label: t('tabs.overview'), icon: 'users' },
      { id: 'growth', label: t('tabs.growth'), icon: 'trending-up' },
      { id: 'market', label: t('tabs.market'), icon: 'home' },
    ],
    [t]
  )
  // Build filter items for read-only display
  const filterItems: FilterItem[] = useMemo(() => {
    if (!readOnly) return []
    return [
      {
        label: t('filters.location'),
        values: {
          value: getLocationLabel(),
          icon: getLocationIcon(locationType) as any,
        },
      },
    ]
  }, [readOnly, locationType, getLocationLabel, t])

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <Box>{t('states.loading')}</Box>
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <ErrorContainer>
          <Box>{t('states.error', { error })}</Box>
        </ErrorContainer>
      )
    }

    if (!data) {
      return (
        <LoadingContainer>
          <Box>{t('states.noData')}</Box>
        </LoadingContainer>
      )
    }

    return renderTabContent()
  }

  return (
    <DemographicsContainer>
      <AnalysisSectionHeader title={t('title')} subtitle={t('subtitle')} />

      {/* Location Selector */}
      <FilterSection>
        <FilterBar>
          <LocationFilters>
            {!readOnly ? (
              <>
                {/* Location Type Chips */}
                {cityName && (
                  <StyledFilterChip
                    $active={locationType === 'city'}
                    onClick={() => onLocationTypeChange('city')}
                  >
                    <Icon name="building-2" size="small" />
                    {cityName}
                  </StyledFilterChip>
                )}
                {districtName && (
                  <StyledFilterChip
                    $active={locationType === 'district'}
                    onClick={() => onLocationTypeChange('district')}
                  >
                    <Icon name="map" size="small" />
                    {districtName}
                  </StyledFilterChip>
                )}

                {/* Travel Mode Chips */}
                {(cityName || districtName) && <Divider />}
                <StyledFilterChip
                  $active={locationType === 'walk'}
                  onClick={() => onLocationTypeChange('walk')}
                >
                  <Icon name="footprints" size="small" />
                  {t('filters.byWalk')}
                </StyledFilterChip>
                <StyledFilterChip
                  $active={locationType === 'bicycle'}
                  onClick={() => onLocationTypeChange('bicycle')}
                >
                  <Icon name="bike" size="small" />
                  {t('filters.byBicycle')}
                </StyledFilterChip>
                <StyledFilterChip
                  $active={locationType === 'car'}
                  onClick={() => onLocationTypeChange('car')}
                >
                  <Icon name="car" size="small" />
                  {t('filters.byCar')}
                </StyledFilterChip>

                {/* Dynamic selectors based on active type */}
                {locationType === 'radius' && (
                  <>
                    <Divider />
                    <FilterSelect
                      value={radiusDistance?.toString() ?? ''}
                      onChange={(value) =>
                        onRadiusDistanceChange(parseInt(value, 10))
                      }
                      options={[
                        {
                          value: '500',
                          label: t('filters.options.radius.500m'),
                        },
                        {
                          value: '1000',
                          label: t('filters.options.radius.1km'),
                        },
                        {
                          value: '2000',
                          label: t('filters.options.radius.2km'),
                        },
                        {
                          value: '5000',
                          label: t('filters.options.radius.5km'),
                        },
                      ]}
                    />
                  </>
                )}

                {locationType === 'walk' && (
                  <>
                    <Divider />
                    <FilterSelect
                      value={walkTime?.toString() ?? ''}
                      onChange={(value) =>
                        onWalkTimeChange(parseInt(value, 10))
                      }
                      options={[
                        { value: '5', label: t('filters.options.time.5min') },
                        { value: '10', label: t('filters.options.time.10min') },
                        { value: '15', label: t('filters.options.time.15min') },
                      ]}
                    />
                  </>
                )}

                {locationType === 'bicycle' && (
                  <>
                    <Divider />
                    <FilterSelect
                      value={bicycleTime?.toString() ?? ''}
                      onChange={(value) =>
                        onBicycleTimeChange(parseInt(value, 10))
                      }
                      options={[
                        { value: '5', label: t('filters.options.time.5min') },
                        { value: '15', label: t('filters.options.time.15min') },
                        { value: '30', label: t('filters.options.time.30min') },
                      ]}
                    />
                  </>
                )}

                {locationType === 'car' && (
                  <>
                    <Divider />
                    <FilterSelect
                      value={carTime?.toString() ?? ''}
                      onChange={(value) => onCarTimeChange(parseInt(value, 10))}
                      options={[
                        { value: '10', label: t('filters.options.time.10min') },
                        { value: '20', label: t('filters.options.time.20min') },
                        { value: '30', label: t('filters.options.time.30min') },
                      ]}
                    />
                  </>
                )}

                {/* Current Location Badge */}
                <LocationBadge>
                  <Icon name="map-pin" size="small" />
                  {getLocationLabel()}
                </LocationBadge>
              </>
            ) : (
              /* Read-only mode: show active filter using FilterDisplay */
              <FilterDisplay filters={filterItems} showDividers={false} />
            )}
          </LocationFilters>
        </FilterBar>
      </FilterSection>

      <AnalysisTabButtonMenu
        tabs={demographicsTabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />

      <ContentContainer>{renderContent()}</ContentContainer>
    </DemographicsContainer>
  )
}
